import React, { createContext, useContext } from 'react';
import { Modal, Pressable, ScrollView, Text, View } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { BottomTabBarProps } from '@react-navigation/bottom-tabs';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { RADIUS, ThemePreference } from '../assets/theme';
import { makeStyles, useAppTheme } from '../theme/ThemeContext';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { isAdminRole, logout } from '../redux/slices/authSlice';
import Avatar from '../components/ui/Avatar';
import FilterChip from '../components/ui/FilterChip';
import ThemeToggle from '../components/ui/ThemeToggle';
import BrandMark from './BrandMark';
import { MainTabParamList, NAV_ITEMS, roleLabel, visibleNavItems } from './navItems';
import type { RootStackParamList } from './AppNavigator';

type MobileMenuContextValue = { openMenu: () => void };

export const MobileMenuContext = createContext<MobileMenuContextValue>({ openMenu: () => {} });

/** Mobile top bar for section screens: logo on the left, theme toggle + avatar on the right. */
export function MobileHeader() {
  const styles = useStyles();
  const insets = useSafeAreaInsets();
  const username = useAppSelector((s) => s.auth.username);
  const { openMenu } = useContext(MobileMenuContext);

  return (
    <View style={[styles.header, { paddingTop: insets.top }]}>
      <BrandMark size={32} />
      <View style={styles.headerActions}>
        <ThemeToggle variant="ghost" size={40} />
        <Pressable onPress={openMenu} accessibilityRole="button" accessibilityLabel="Ouvrir le menu du compte" hitSlop={6}>
          <Avatar name={username} size={34} />
        </Pressable>
      </View>
    </View>
  );
}

/** Airbnb-style bottom tab bar: 4 main sections + a "Menu" tab opening the sheet. */
export function MobileTabBar({ state, navigation }: BottomTabBarProps) {
  const styles = useStyles();
  const { colors } = useAppTheme();
  const insets = useSafeAreaInsets();
  const { openMenu } = useContext(MobileMenuContext);
  const activeName = state.routes[state.index]?.name;
  const tabs = NAV_ITEMS.filter((item) => item.mobileTab && state.routeNames.includes(item.name));
  const menuActive = !tabs.some((item) => item.name === activeName);

  return (
    <View style={[styles.tabBar, { paddingBottom: Math.max(insets.bottom, 6) }]}>
      {tabs.map((item) => {
        const focused = item.name === activeName;
        const color = focused ? colors.primary : colors.textSecondary;
        return (
          <Pressable
            key={item.name}
            accessibilityRole="tab"
            accessibilityState={{ selected: focused }}
            accessibilityLabel={item.label}
            onPress={() => {
              const route = state.routes.find((r) => r.name === item.name);
              const event = navigation.emit({ type: 'tabPress', target: route?.key, canPreventDefault: true });
              if (!focused && !event.defaultPrevented) navigation.navigate(item.name);
            }}
            style={styles.tabItem}
          >
            <MaterialCommunityIcons name={focused ? item.activeIcon : item.icon} size={24} color={color} />
            <Text style={[styles.tabLabel, { color }, focused && styles.tabLabelActive]} numberOfLines={1}>
              {item.shortLabel ?? item.label}
            </Text>
          </Pressable>
        );
      })}
      <Pressable
        accessibilityRole="tab"
        accessibilityState={{ selected: menuActive }}
        accessibilityLabel="Menu"
        onPress={openMenu}
        style={styles.tabItem}
      >
        <MaterialCommunityIcons name="menu" size={24} color={menuActive ? colors.primary : colors.textSecondary} />
        <Text
          style={[styles.tabLabel, { color: menuActive ? colors.primary : colors.textSecondary }, menuActive && styles.tabLabelActive]}
        >
          Menu
        </Text>
      </Pressable>
    </View>
  );
}

const THEME_CHOICES: { value: ThemePreference; label: string; icon: 'white-balance-sunny' | 'weather-night' | 'theme-light-dark' }[] = [
  { value: 'light', label: 'Jour', icon: 'white-balance-sunny' },
  { value: 'dark', label: 'Nuit', icon: 'weather-night' },
  { value: 'system', label: 'Auto', icon: 'theme-light-dark' },
];

type MobileMenuSheetProps = {
  visible: boolean;
  onClose: () => void;
  activeName?: keyof MainTabParamList;
};

/** Bottom sheet with the remaining sections, appearance and logout. */
export function MobileMenuSheet({ visible, onClose, activeName }: MobileMenuSheetProps) {
  const styles = useStyles();
  const { colors, preference, setPreference } = useAppTheme();
  const insets = useSafeAreaInsets();
  const dispatch = useAppDispatch();
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const username = useAppSelector((s) => s.auth.username);
  const role = useAppSelector((s) => s.auth.role);
  const isAdmin = isAdminRole(role);
  const sections = visibleNavItems(isAdmin).filter((item) => !item.mobileTab);

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.sheetRoot}>
        <Pressable style={styles.backdrop} onPress={onClose} accessibilityLabel="Fermer le menu" />
        <View style={[styles.sheet, { paddingBottom: insets.bottom + 16 }]}>
          <View style={styles.handle} />
          <ScrollView showsVerticalScrollIndicator={false}>
            <View style={styles.profile}>
              <Avatar name={username} size={48} />
              <View style={styles.profileText}>
                <Text style={styles.profileName} numberOfLines={1}>{username ?? 'Utilisateur'}</Text>
                <Text style={styles.profileRole}>{roleLabel(role)}</Text>
              </View>
            </View>

            <Text style={styles.sheetLabel}>Gestion</Text>
            <View style={styles.rows}>
              {sections.map((item, index) => {
                const active = item.name === activeName;
                return (
                  <Pressable
                    key={item.name}
                    accessibilityRole="button"
                    accessibilityState={{ selected: active }}
                    onPress={() => {
                      onClose();
                      navigation.navigate('Main', { screen: item.name });
                    }}
                    style={({ pressed }) => [
                      styles.row,
                      index > 0 && styles.rowBorder,
                      pressed && styles.rowPressed,
                    ]}
                  >
                    <View style={[styles.rowIcon, active && styles.rowIconActive]}>
                      <MaterialCommunityIcons
                        name={active ? item.activeIcon : item.icon}
                        size={20}
                        color={active ? colors.primary : colors.textPrimary}
                      />
                    </View>
                    <Text style={[styles.rowLabel, active && styles.rowLabelActive]}>{item.label}</Text>
                    <MaterialCommunityIcons name="chevron-right" size={22} color={colors.textMuted} />
                  </Pressable>
                );
              })}
            </View>

            <Text style={styles.sheetLabel}>Apparence</Text>
            <View style={styles.themeRow}>
              {THEME_CHOICES.map((choice) => (
                <FilterChip
                  key={choice.value}
                  label={choice.label}
                  icon={choice.icon}
                  selected={preference === choice.value}
                  onPress={() => setPreference(choice.value)}
                  style={styles.themeChip}
                />
              ))}
            </View>

            <Pressable
              accessibilityRole="button"
              onPress={() => { onClose(); dispatch(logout()); }}
              style={({ pressed }) => [styles.logout, pressed && styles.rowPressed]}
            >
              <MaterialCommunityIcons name="logout" size={20} color={colors.textPrimary} />
              <Text style={styles.logoutText}>Déconnexion</Text>
            </Pressable>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

const useStyles = makeStyles((c) => ({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    backgroundColor: c.background,
    borderBottomWidth: 1,
    borderBottomColor: c.divider,
    minHeight: 60,
  },
  headerActions: { flexDirection: 'row', alignItems: 'center', gap: 6 },

  tabBar: {
    flexDirection: 'row',
    backgroundColor: c.background,
    borderTopWidth: 1,
    borderTopColor: c.divider,
    paddingTop: 6,
  },
  tabItem: { flex: 1, alignItems: 'center', justifyContent: 'center', minHeight: 50, gap: 2 },
  tabLabel: { fontSize: 11, fontWeight: '600' },
  tabLabelActive: { fontWeight: '700' },

  sheetRoot: { flex: 1, justifyContent: 'flex-end' },
  backdrop: { ...({ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 } as const), backgroundColor: c.overlay },
  sheet: {
    backgroundColor: c.surface,
    borderTopLeftRadius: RADIUS.xl,
    borderTopRightRadius: RADIUS.xl,
    paddingHorizontal: 20,
    paddingTop: 8,
    maxHeight: '88%',
  },
  handle: {
    alignSelf: 'center',
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: c.border,
    marginBottom: 12,
  },
  profile: { flexDirection: 'row', alignItems: 'center', gap: 14, paddingVertical: 8, marginBottom: 8 },
  profileText: { flexShrink: 1 },
  profileName: { fontSize: 18, fontWeight: '700', color: c.textPrimary },
  profileRole: { fontSize: 14, color: c.textSecondary, marginTop: 2 },
  sheetLabel: {
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: c.textMuted,
    marginTop: 16,
    marginBottom: 8,
  },
  rows: { borderRadius: RADIUS.lg, borderWidth: 1, borderColor: c.divider, overflow: 'hidden' },
  row: { flexDirection: 'row', alignItems: 'center', gap: 14, paddingHorizontal: 14, minHeight: 56 },
  rowBorder: { borderTopWidth: 1, borderTopColor: c.divider },
  rowPressed: { backgroundColor: c.surfaceAlt },
  rowIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: c.surfaceAlt,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowIconActive: { backgroundColor: c.primarySoft },
  rowLabel: { flex: 1, fontSize: 16, fontWeight: '500', color: c.textPrimary },
  rowLabelActive: { fontWeight: '700' },
  themeRow: { flexDirection: 'row', gap: 8 },
  themeChip: { flex: 1, justifyContent: 'center' },
  logout: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginTop: 20,
    paddingVertical: 14,
    paddingHorizontal: 4,
    borderTopWidth: 1,
    borderTopColor: c.divider,
  },
  logoutText: { fontSize: 16, fontWeight: '600', color: c.textPrimary, textDecorationLine: 'underline' },
}));
